<template>
    <Card pageTitle="Cova Import Wizard">
        <AllowPermission :permission="PERMISSION_CODES.CMS_COVA_STOCK_IMPORT">
            <div class="cova-import-page">
                <div class="wizard-header">
                    <div>
                        <div class="eyebrow">Cova migration</div>
                        <h2>{{ pageHeading }}</h2>
                        <p>{{ pageDescription }}</p>
                    </div>
                    <div class="header-actions">
                        <router-link to="/cova-import">
                            <Button borderOnly noMargin type="button">Back to Cova Sync</Button>
                        </router-link>
                        <router-link to="/stock-import">
                            <Button borderOnly noMargin type="button">Classic Import</Button>
                        </router-link>
                        <router-link to="/purchase-orders">
                            <Button borderOnly noMargin type="button">Purchase Orders</Button>
                        </router-link>
                    </div>
                </div>

                <div class="step-rail">
                    <button
                        v-for="(step, index) in steps"
                        :key="step.key"
                        type="button"
                        :class="['step-pill', { active: activeStep === step.key, done: completedSteps.includes(step.key) }]"
                        @click="activeStep = step.key"
                    >
                        <span class="step-index">{{ index + 1 }}</span>
                        <span>{{ step.label }}</span>
                    </button>
                </div>

                <section class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Upload and analyze</h3>
                            <p>{{ uploadDescription }}</p>
                        </div>
                        <span class="section-status" :class="{ ready: filesReady }">
                            {{ filesReady ? "Files ready" : "Waiting for files" }}
                        </span>
                    </div>

                    <div class="mode-warning">
                        {{ modeWarningText }}
                    </div>

                    <div class="form-grid form-grid--compact">
                        <div class="field-block">
                            <label>File setup</label>
                            <select v-model="form.fileMode" class="policy-select" :disabled="inputsLocked">
                                <option value="single">One workbook with product + inventory sheets</option>
                                <option value="separate">Separate product catalog and inventory files</option>
                                <option value="catalog-only">Catalogue only - products/images, no stock</option>
                            </select>
                        </div>
                        <div class="field-block">
                            <label>Image handling</label>
                            <select v-model="form.imageStrategy" class="policy-select" :disabled="inputsLocked">
                                <option value="url">Use Cova URLs for this import</option>
                                <option value="mirror">Mirror images to High Ties storage</option>
                            </select>
                        </div>
                    </div>

                    <div class="file-grid">
                        <label class="file-drop">
                            <span class="file-title">{{ primaryFileTitle }}</span>
                            <span class="file-name">{{ productFileName || primaryFilePlaceholder }}</span>
                            <input
                                ref="productFileInput"
                                type="file"
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                :disabled="inputsLocked"
                                @change="handleFileSelect('productFile', $event)"
                            />
                        </label>
                        <div v-if="usesSingleWorkbook" class="file-drop file-drop--locked">
                            <span class="file-title">Inventory sheet</span>
                            <span class="file-name">{{ productFileName ? "Using " + productFileName : "Included in the same workbook" }}</span>
                        </div>
                        <div v-else-if="isCatalogOnlyMode" class="file-drop file-drop--locked">
                            <span class="file-title">Inventory export</span>
                            <span class="file-name">Not used for catalogue-only sync</span>
                        </div>
                        <label v-else class="file-drop">
                            <span class="file-title">Inventory export</span>
                            <span class="file-name">{{ stockFileName || "Choose Cova inventory file" }}</span>
                            <input
                                ref="stockFileInput"
                                type="file"
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                :disabled="inputsLocked"
                                @change="handleFileSelect('stockFile', $event)"
                            />
                        </label>
                    </div>

                    <div class="form-grid form-grid--single">
                        <CustomInput
                            v-model="form.supplierInvoiceNumber"
                            label="Import reference"
                            placeholder="COVA-IMPORT-YYYY-MM-DD"
                            :disabled="inputsLocked"
                        />
                        <CustomInput v-model="form.notes" label="Notes" placeholder="Optional internal note" :disabled="inputsLocked" />
                    </div>

                    <div class="action-row">
                        <Button type="button" :disabled="!canRunPreflight" @click="runPreflight">
                            {{ preflighting ? "Analyzing..." : "Run Analysis" }}
                        </Button>
                        <Button v-if="importResult" borderOnly noMargin type="button" :disabled="postingInventory" @click="resetWizard">
                            Start New Import
                        </Button>
                    </div>
                </section>

                <section v-if="!isCatalogOnlyMode" class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Destination and supplier mapping</h3>
                            <p>Map the detected Cova location and suppliers to High Ties records.</p>
                        </div>
                        <span class="section-status" :class="{ ready: mappingReady }">
                            {{ mappingReady ? "Mapped" : preflight ? "Needs mapping" : "Not checked" }}
                        </span>
                    </div>

                    <UiState v-if="preflighting" type="loading" title="Reading Cova files" compact />
                    <UiState
                        v-else-if="!preflight"
                        type="empty"
                        title="No analysis yet"
                        message="Upload the Cova file setup and run analysis before mapping suppliers."
                        compact
                    />
                    <div v-else>
                        <div class="facts-grid">
                            <div class="fact-panel">
                                <span class="report-label">Product catalog</span>
                                <strong>{{ numberFormat(productFact.rows) }} rows</strong>
                                <p>{{ productFact.fileName || "Catalog file" }} - {{ productFact.sheetName || "Selected sheet" }}</p>
                            </div>
                            <div class="fact-panel">
                                <span class="report-label">Inventory count</span>
                                <strong>{{ numberFormat(inventoryFact.positiveRows) }} positive rows</strong>
                                <p>{{ inventoryFact.fileName || "Inventory file" }} - {{ inventoryFact.sheetName || "Selected sheet" }}</p>
                                <p v-if="inventoryFact.sharedWorkbook">Same workbook as product catalog</p>
                            </div>
                            <div class="fact-panel">
                                <span class="report-label">Cova file location</span>
                                <strong>{{ detectedLocationLabel }}</strong>
                                <p>{{ detectedRoomLabel }}</p>
                            </div>
                        </div>

                        <div class="mapping-row">
                            <SelectDropdown
                                label="Target High Ties location"
                                :options="warehouseOptions"
                                :multiple="false"
                                :value="form.warehouseId"
                                :disabled="inputsLocked"
                                @change="setWarehouse($event)"
                            />
                        </div>

                        <div class="mapping-table" v-if="detectedSuppliers.length">
                            <div class="preview-heading">Detected Cova suppliers</div>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Cova supplier</th>
                                        <th>Rows</th>
                                        <th>Recommendation</th>
                                        <th>Action</th>
                                        <th>Supplier choice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="supplier in detectedSuppliers" :key="supplier.covaSupplierName">
                                        <td>{{ supplier.covaSupplierName }}</td>
                                        <td>{{ numberFormat(supplier.rows) }}</td>
                                        <td>
                                            <span v-if="supplier.matchedSupplierName" class="status-chip ready">
                                                {{ supplier.matchedSupplierName }}
                                            </span>
                                            <span v-else class="status-chip">Manual</span>
                                        </td>
                                        <td class="supplier-action-cell">
                                            <select
                                                class="policy-select mapping-action-select"
                                                :value="getSupplierMapping(supplier.covaSupplierName).action"
                                                :disabled="inputsLocked"
                                                @change="setSupplierMappingAction(supplier.covaSupplierName, $event.target.value)"
                                            >
                                                <option value="">Needs review</option>
                                                <option value="existing">Use existing supplier</option>
                                                <option value="create">Create new supplier</option>
                                            </select>
                                        </td>
                                        <td class="supplier-choice-cell">
                                            <SelectDropdown
                                                v-if="getSupplierMapping(supplier.covaSupplierName).action === 'existing'"
                                                :options="supplierOptions"
                                                :multiple="false"
                                                :value="getSupplierMapping(supplier.covaSupplierName).supplierId"
                                                placeholder="Select supplier"
                                                :disabled="inputsLocked"
                                                @change="setSupplierMappingSupplier(supplier.covaSupplierName, $event)"
                                            />
                                            <div v-else-if="getSupplierMapping(supplier.covaSupplierName).action === 'create'" class="create-supplier-field">
                                                <input
                                                    class="supplier-name-input"
                                                    :class="{ invalid: !isValidCreateSupplierName(getSupplierMapping(supplier.covaSupplierName).createSupplierName) }"
                                                    :value="getSupplierMapping(supplier.covaSupplierName).createSupplierName"
                                                    :disabled="inputsLocked"
                                                    placeholder="New supplier name"
                                                    @input="setSupplierCreateName(supplier.covaSupplierName, $event.target.value)"
                                                />
                                                <span class="helper-text">
                                                    {{ isValidCreateSupplierName(getSupplierMapping(supplier.covaSupplierName).createSupplierName) ? "Will be created during import" : "Enter a supplier name" }}
                                                </span>
                                            </div>
                                            <span v-else class="mapping-help">{{ supplier.needsManualReview ? "Choose an existing supplier or enter a new supplier name." : "Confirm how this supplier should be handled." }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Review report</h3>
                            <p>{{ reviewDescription }}</p>
                        </div>
                        <span class="section-status" :class="{ ready: preflightEffectiveReady }">
                            {{ preflightEffectiveReady ? "Passed" : preflight ? "Needs review" : "Not checked" }}
                        </span>
                    </div>

                    <UiState v-if="preflighting" type="loading" title="Checking Cova files" compact />
                    <UiState
                        v-else-if="!preflight"
                        type="empty"
                        title="No report yet"
                        message="Run analysis to see the import report."
                        compact
                    />
                    <div v-else>
                        <div class="metric-grid">
                            <div v-for="metric in summaryMetrics" :key="metric.label" class="metric-cell">
                                <span>{{ metric.label }}</span>
                                <strong>{{ metric.value }}</strong>
                            </div>
                        </div>

                        <div v-if="preflight.blockingErrors && preflight.blockingErrors.length" :class="['issue-list', unhandledBlockingErrors.length ? 'danger' : 'warning']">
                            <h4>Blocking review</h4>
                            <div v-for="issue in preflight.blockingErrors" :key="issue.code" class="issue-row">
                                <i class="material-icons">{{ isAcknowledgedIssue(issue) ? "check_circle" : "error_outline" }}</i>
                                <span>{{ issue.message }}</span>
                            </div>
                        </div>

                        <div v-if="needsDuplicatePackageAck" class="duplicate-package-options">
                            <label class="ack-row ack-row--safe">
                                <input v-model="form.skipExistingPackageIds" type="checkbox" :disabled="inputsLocked" />
                                <span>
                                    <strong>Skip existing Cova package IDs and import only new packages.</strong>
                                    <small>Recommended when this export includes packages already in High Ties.</small>
                                </span>
                            </label>
                            <label class="ack-row">
                                <input v-model="form.allowDuplicatePackageIds" type="checkbox" :disabled="inputsLocked" />
                                <span>
                                    <strong>I reviewed the existing Cova package IDs and want to allow duplicate package IDs.</strong>
                                    <small>Use only when you intentionally want the same package IDs included again.</small>
                                </span>
                            </label>
                        </div>

                        <div v-if="preflight.warnings && preflight.warnings.length" class="issue-list warning">
                            <h4>Warnings</h4>
                            <div v-for="issue in preflight.warnings" :key="issue.code" class="issue-row">
                                <i class="material-icons">report_problem</i>
                                <span>{{ issue.message }}</span>
                            </div>
                        </div>

                        <div v-if="supplierCreateRows.length" class="issue-list neutral">
                            <h4>Suppliers to create</h4>
                            <div v-for="row in supplierCreateRows" :key="row.covaSupplierName" class="issue-row">
                                <i class="material-icons">add_business</i>
                                <span>{{ row.createSupplierName }} will be created from Cova supplier "{{ row.covaSupplierName }}".</span>
                            </div>
                        </div>

                        <div class="report-columns">
                            <div v-if="productsToCreateSample.length" class="preview-table">
                                <div class="preview-heading">Sample products to create</div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Catalog SKU</th>
                                            <th>Product</th>
                                            <th>Image</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, index) in productsToCreateSample" :key="`${row.catalogSku || 'product'}-${index}`">
                                            <td>{{ row.catalogSku }}</td>
                                            <td>{{ row.name }}</td>
                                            <td>{{ row.image ? "Found" : "Missing" }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-if="existingPackageSamples.length" class="preview-table">
                                <div class="preview-heading">Existing package ID sample</div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Package ID</th>
                                            <th>Item</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, index) in existingPackageSamples" :key="`${row.packageId || 'package'}-${index}`">
                                            <td>{{ row.packageId }}</td>
                                            <td>{{ row.itemName || row.itemCode }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>{{ importSectionHeading }}</h3>
                            <p>{{ importSectionDescription }}</p>
                        </div>
                        <span class="section-status" :class="{ ready: isCatalogOnlyMode ? importResult : postedResult }">
                            {{ importSectionStatus }}
                        </span>
                    </div>

                    <div class="operation-grid">
                        <div class="operation-step">
                            <div class="operation-icon" :class="{ done: importResult }">
                                <i class="material-icons">{{ importResult ? "check" : "upload_file" }}</i>
                            </div>
                            <div>
                                <h4>{{ importOperationTitle }}</h4>
                                <p>{{ importPlanLabel }}</p>
                                <Button type="button" :disabled="!canImport || importing || !!importResult" @click="runImport">
                                    {{ importButtonLabel }}
                                </Button>
                            </div>
                        </div>

                        <div v-if="!isCatalogOnlyMode" class="operation-step">
                            <div class="operation-icon" :class="{ done: postedResult }">
                                <i class="material-icons">{{ postedResult ? "check" : "inventory" }}</i>
                            </div>
                            <div>
                                <h4>Receive all and post inventory</h4>
                                <p>Posts every created purchase order into the POS stock ledger.</p>
                                <Button type="button" :disabled="postInventoryDisabled" @click="postInventory">
                                    {{ postInventoryButtonLabel }}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div v-if="operationProgressVisible" class="operation-progress">
                        <div class="progress-heading">
                            <span>{{ operationProgress.label }}</span>
                            <strong>{{ operationProgressPercent }}%</strong>
                        </div>
                        <div
                            class="progress-track"
                            role="progressbar"
                            :aria-valuenow="operationProgressPercent"
                            aria-valuemin="0"
                            aria-valuemax="100"
                        >
                            <div class="progress-fill" :style="{ width: `${operationProgressPercent}%` }"></div>
                        </div>
                        <div v-if="operationProgress.detail" class="progress-detail">{{ operationProgress.detail }}</div>
                    </div>

                    <div v-if="purchaseOrders.length && !hasPurchaseOrderEditPermission && !postedResult" class="issue-list warning">
                        <h4>Permission required</h4>
                        <div class="issue-row">
                            <i class="material-icons">lock</i>
                            <span>Purchase-order edit access is required before posting this import to POS.</span>
                        </div>
                    </div>

                    <div v-if="importWarnings.length" class="issue-list warning">
                        <h4>Import warnings</h4>
                        <div v-for="warning in importWarnings" :key="warning.id" class="issue-row">
                            <i class="material-icons">report_problem</i>
                            <span>{{ warning.message }}</span>
                        </div>
                    </div>

                    <div v-if="postedWarnings.length" class="issue-list warning">
                        <h4>Post warnings</h4>
                        <div v-for="warning in postedWarnings" :key="warning.id" class="issue-row">
                            <i class="material-icons">report_problem</i>
                            <span>{{ warning.message }}</span>
                        </div>
                    </div>

                    <div v-if="purchaseOrders.length || postedResult || importResult" class="final-report">
                        <div>
                            <span class="report-label">Purchase orders</span>
                            <strong>{{ numberFormat(purchaseOrders.length) }}</strong>
                        </div>
                        <div v-if="isCatalogOnlyMode">
                            <span class="report-label">Products queued</span>
                            <strong>{{ numberFormat(importedProductCount) }}</strong>
                        </div>
                        <div v-if="isCatalogOnlyMode">
                            <span class="report-label">Stock changes</span>
                            <strong>0</strong>
                        </div>
                        <div v-if="postedResult">
                            <span class="report-label">Received rows</span>
                            <strong>{{ numberFormat(postedResult.receivedRows) }}</strong>
                        </div>
                        <div v-if="postedResult">
                            <span class="report-label">Received units</span>
                            <strong>{{ numberFormat(postedResult.receivedQty) }}</strong>
                        </div>
                        <div v-if="showImageMirrorReport">
                            <span class="report-label">Images mirrored</span>
                            <strong>{{ numberFormat(imageMirrorResult.mirrored) }}</strong>
                        </div>
                        <div v-if="createdSuppliers.length">
                            <span class="report-label">Suppliers created</span>
                            <strong>{{ numberFormat(createdSuppliers.length) }}</strong>
                        </div>
                        <div v-if="imageMirrorResult && imageMirrorResult.failed">
                            <span class="report-label">Mirror failures</span>
                            <strong>{{ numberFormat(imageMirrorResult.failed) }}</strong>
                        </div>
                        <div v-if="operationWarningCount">
                            <span class="report-label">Warnings</span>
                            <strong>{{ numberFormat(operationWarningCount) }}</strong>
                        </div>
                    </div>

                    <div v-if="purchaseOrderRows.length" class="preview-table">
                        <div class="preview-heading">Created purchase orders</div>
                        <table>
                            <thead>
                                <tr>
                                    <th>PO</th>
                                    <th>Cova supplier</th>
                                    <th>High Ties supplier</th>
                                    <th>Supplier action</th>
                                    <th>Rows</th>
                                    <th>Units</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in purchaseOrderRows" :key="row.purchaseOrderId || row.supplierInvoiceNumber">
                                    <td>{{ row.purchaseOrderName }}</td>
                                    <td>{{ row.covaSupplierName }}</td>
                                    <td>{{ row.supplierName }}</td>
                                    <td>{{ row.supplierCreated ? "Created" : "Existing" }}</td>
                                    <td>{{ numberFormat(row.rows) }}</td>
                                    <td>{{ numberFormat(row.units) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="purchaseOrders.length" class="report-actions">
                        <router-link v-if="purchaseOrders[0]" :to="`/purchase-orders/${purchaseOrders[0].name}`">
                            <Button borderOnly noMargin type="button">Open First PO</Button>
                        </router-link>
                        <router-link to="/inventory-manager">
                            <Button borderOnly noMargin type="button">View Inventory</Button>
                        </router-link>
                    </div>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Button from "../../components/UI/Button.vue"
import Card from "../../components/UI/Card.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import UiState from "../../components/UI/UiState.vue"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import { consumeCovaSyncHandoff } from "../../utils/cova-sync-session"

export default {
    name: "CovaImportWizard",
    components: {
        AllowPermission,
        Button,
        Card,
        CustomInput,
        SelectDropdown,
        UiState,
    },
    data() {
        return {
            PERMISSION_CODES,
            activeStep: "upload",
            form: {
                fileMode: "single",
                warehouseId: "",
                supplierInvoiceNumber: `COVA-IMPORT-${new Date().toISOString().slice(0, 10)}`,
                notes: "Created from Cova import wizard",
                imageStrategy: "url",
                allowDuplicatePackageIds: false,
                skipExistingPackageIds: false,
            },
            supplierMappings: {},
            files: {
                productFile: null,
                stockFile: null,
            },
            preflighting: false,
            importing: false,
            postingInventory: false,
            preflight: null,
            importResult: null,
            postedResult: null,
            postedPurchaseOrderResults: {},
            preflightRequestId: 0,
            importRequestId: 0,
            operationProgress: {
                active: false,
                label: "",
                detail: "",
                percent: 0,
            },
            operationProgressTimer: null,
            operationProgressClearTimer: null,
        }
    },
    computed: {
        ...mapState("misc", ["warehouses", "suppliers"]),
        ...mapState("auth", ["user"]),
        isCatalogOnlyMode() {
            return this.form.fileMode === "catalog-only"
        },
        pageHeading() {
            return this.isCatalogOnlyMode ? "Sync product catalogue" : "Import products and inventory"
        },
        pageDescription() {
            return this.isCatalogOnlyMode
                ? "Create missing High Ties products from the Cova catalogue export and refresh product images without changing stock."
                : "Analyze the Cova export, map detected suppliers, review the report, then post inventory to POS."
        },
        uploadDescription() {
            return this.isCatalogOnlyMode
                ? "Upload a Cova product catalogue export. Inventory files are not used in this mode."
                : "Use one workbook with product and inventory sheets, or attach separate Cova exports."
        },
        modeWarningText() {
            return this.isCatalogOnlyMode
                ? "Catalogue-only sync creates missing products without creating purchase orders, receiving stock, or changing inventory quantities."
                : "This wizard creates products and purchase orders for receiving. Use Cova Reconciliation for daily inventory-only stock updates."
        },
        reviewDescription() {
            return this.isCatalogOnlyMode
                ? "Confirm which products and image references will be synced. No stock rows or purchase orders will be posted."
                : "Confirm what will be created, skipped, updated, or split into purchase orders."
        },
        importSectionHeading() {
            return this.isCatalogOnlyMode ? "Sync catalogue" : "Import and post"
        },
        importSectionDescription() {
            return this.isCatalogOnlyMode
                ? "Create missing products using the selected image handling. Stock is not touched."
                : "Create missing products, create supplier purchase orders, then receive stock into POS."
        },
        importSectionStatus() {
            if (this.isCatalogOnlyMode) return this.importResult ? "Synced" : "Not synced"
            return this.postedResult ? "Posted" : this.purchaseOrders.length ? "POs created" : "Not imported"
        },
        importOperationTitle() {
            return this.isCatalogOnlyMode ? "Create products and image references" : "Create products and purchase orders"
        },
        steps() {
            if (this.isCatalogOnlyMode) {
                return [
                    { key: "upload", label: "Upload" },
                    { key: "review", label: "Review" },
                    { key: "import", label: "Sync" },
                ]
            }
            return [
                { key: "upload", label: "Upload" },
                { key: "map", label: "Map" },
                { key: "review", label: "Review" },
                { key: "import", label: "Import" },
                { key: "post", label: "Post" },
            ]
        },
        supplierOptions() {
            return this.suppliers.map((item) => ({ id: item.id, title: item.name }))
        },
        warehouseOptions() {
            return this.warehouses.map((item) => ({ id: item.id, title: item.name }))
        },
        productFileName() {
            return this.files.productFile ? this.files.productFile.name : ""
        },
        stockFileName() {
            return this.files.stockFile ? this.files.stockFile.name : ""
        },
        usesSingleWorkbook() {
            return this.form.fileMode === "single"
        },
        primaryFileTitle() {
            return this.usesSingleWorkbook ? "Cova workbook" : "Product catalog export"
        },
        primaryFilePlaceholder() {
            return this.isCatalogOnlyMode
                ? "Choose Cova product catalog file"
                : this.usesSingleWorkbook
                ? "Choose workbook with product and inventory sheets"
                : "Choose Cova catalog file"
        },
        effectiveStockFile() {
            if (this.isCatalogOnlyMode) return null
            return this.usesSingleWorkbook ? this.files.productFile : this.files.stockFile
        },
        filesReady() {
            return !!this.files.productFile && (this.isCatalogOnlyMode || this.usesSingleWorkbook || !!this.files.stockFile)
        },
        productFact() {
            return (this.preflight && this.preflight.fileFacts && this.preflight.fileFacts.productCatalog) || {}
        },
        inventoryFact() {
            return (this.preflight && this.preflight.fileFacts && this.preflight.fileFacts.inventory) || {}
        },
        detectedSuppliers() {
            return (this.preflight && this.preflight.suppliers) || []
        },
        detectedLocations() {
            return (this.preflight && this.preflight.locations) || []
        },
        detectedRooms() {
            return (this.preflight && this.preflight.rooms) || []
        },
        detectedLocationLabel() {
            if (!this.detectedLocations.length) return "No location"
            if (this.detectedLocations.length === 1) return this.detectedLocations[0].covaLocationName
            return `${this.detectedLocations.length} locations`
        },
        detectedRoomLabel() {
            if (!this.detectedRooms.length) return "No rooms detected"
            return this.detectedRooms.map((room) => `${room.covaRoomName}: ${this.numberFormat(room.rows)}`).join(", ")
        },
        supplierMappingsComplete() {
            if (this.isCatalogOnlyMode) return true
            if (!this.detectedSuppliers.length) return false
            return this.detectedSuppliers.every((supplier) => this.isSupplierMappingComplete(supplier.covaSupplierName))
        },
        mappingReady() {
            if (this.isCatalogOnlyMode) return this.preflightEffectiveReady
            return !!this.preflight && !!this.form.warehouseId && this.supplierMappingsComplete
        },
        supplierCreateRows() {
            return this.detectedSuppliers
                .map((supplier) => ({
                    covaSupplierName: supplier.covaSupplierName,
                    rows: supplier.rows,
                    ...this.getSupplierMapping(supplier.covaSupplierName),
                }))
                .filter((supplier) => supplier.action === "create" && this.isValidCreateSupplierName(supplier.createSupplierName))
        },
        supplierCreateCount() {
            return this.supplierCreateRows.length
        },
        supplierNeedsReviewCount() {
            return this.detectedSuppliers.filter((supplier) => !this.isSupplierMappingComplete(supplier.covaSupplierName)).length
        },
        needsDuplicatePackageAck() {
            return this.hasBlockingCode("POSSIBLE_DUPLICATE_PACKAGE_IDS")
        },
        unhandledBlockingErrors() {
            const errors = (this.preflight && this.preflight.blockingErrors) || []
            return errors.filter((issue) => !this.isAcknowledgedIssue(issue))
        },
        preflightEffectiveReady() {
            return !!this.preflight && this.unhandledBlockingErrors.length === 0
        },
        canImport() {
            return this.preflightEffectiveReady && (this.isCatalogOnlyMode || this.mappingReady)
        },
        purchaseOrders() {
            if (!this.importResult) return []
            if (this.importResult.purchaseOrders && this.importResult.purchaseOrders.length) return this.importResult.purchaseOrders
            return this.importResult.purchaseOrder ? [this.importResult.purchaseOrder] : []
        },
        imageMirrorResult() {
            return this.importResult && this.importResult.imageMirror
        },
        showImageMirrorReport() {
            const result = this.imageMirrorResult
            if (!result) return false
            return (
                this.form.imageStrategy === "mirror" ||
                Number(result.mirrored || 0) > 0 ||
                Number(result.reused || 0) > 0 ||
                Number(result.failed || 0) > 0
            )
        },
        createdSuppliers() {
            return (this.importResult && this.importResult.createdSuppliers) || []
        },
        postedPurchaseOrderResultsList() {
            return Object.values(this.postedPurchaseOrderResults)
        },
        importWarnings() {
            return this.collectImportResultWarnings(this.importResult)
        },
        postedWarnings() {
            return this.normalizeWarningList(this.postedResult && this.postedResult.warnings)
        },
        operationWarningCount() {
            return this.importWarnings.length + this.postedWarnings.length
        },
        pendingPurchaseOrders() {
            return this.purchaseOrders.filter((purchaseOrder) => !this.postedPurchaseOrderResults[purchaseOrder.id])
        },
        importButtonLabel() {
            if (this.importing) {
                if (this.isCatalogOnlyMode) return this.form.imageStrategy === "mirror" ? "Mirroring and Syncing..." : "Syncing..."
                return this.form.imageStrategy === "mirror" ? "Mirroring and Importing..." : "Importing..."
            }
            if (this.isCatalogOnlyMode) return this.importResult ? "Catalogue Synced" : "Sync Catalogue"
            return this.importResult ? "Import Created" : "Import Now"
        },
        postInventoryButtonLabel() {
            if (this.postingInventory) return "Posting..."
            if (this.postedResult) return "Inventory Posted"
            if (this.postedPurchaseOrderResultsList.length) return "Post Remaining to POS"
            return "Post to POS"
        },
        operationProgressVisible() {
            return this.operationProgress.active || (this.operationProgress.percent > 0 && this.operationProgress.percent < 100)
        },
        operationProgressPercent() {
            const percent = Number(this.operationProgress.percent || 0)
            if (!Number.isFinite(percent)) return 0
            return Math.max(0, Math.min(100, Math.round(percent)))
        },
        importPlanLabel() {
            const summary = (this.preflight && this.preflight.summary) || {}
            if (this.isCatalogOnlyMode) {
                const planParts = [
                    this.formatCountLabel(summary.productsToCreate, "product"),
                    this.formatCountLabel(summary.mirrorImageCandidateCount, "image"),
                ]
                return `Creates ${this.joinHumanList(planParts)}. No stock or purchase orders will be changed.`
            }
            const poCount = summary.purchaseOrdersToCreate || this.detectedSuppliers.length || 0
            const planParts = [
                this.formatCountLabel(summary.productsToCreate, "product"),
                this.formatCountLabel(poCount, "supplier purchase order"),
            ]
            if (this.supplierCreateCount) planParts.push(this.formatCountLabel(this.supplierCreateCount, "new supplier"))
            if (summary.skippedExistingPackageRowCount) {
                planParts.push(`${this.formatCountLabel(summary.skippedExistingPackageRowCount, "existing package row")} skipped`)
            }
            return `Creates ${this.joinHumanList(planParts)}.`
        },
        hasPurchaseOrderEditPermission() {
            return checkHasPermission({
                isAdmin: Boolean(this.user && this.user.isAdmin),
                permissionId: PERMISSION_CODES.CMS_EDIT_PURCHASE_ORDER,
                userPermissionIds: (this.user && this.user.permissionIds) || [],
            })
        },
        postInventoryDisabled() {
            return !this.pendingPurchaseOrders.length || this.postingInventory || !!this.postedResult || !this.hasPurchaseOrderEditPermission
        },
        importHasSideEffects() {
            return !!this.importResult
        },
        inputsLocked() {
            return this.preflighting || this.importing || this.postingInventory || this.importHasSideEffects
        },
        canRunPreflight() {
            return this.filesReady && !this.inputsLocked
        },
        productsToCreateSample() {
            return (this.preflight && this.preflight.samples && this.preflight.samples.productsToCreate) || []
        },
        existingPackageSamples() {
            return (this.preflight && this.preflight.samples && this.preflight.samples.existingPackageIds) || []
        },
        purchaseOrderRows() {
            const stockImports = (this.importResult && this.importResult.stockImports) || []
            return stockImports.map((stockImport) => {
                const purchaseOrder = stockImport.purchaseOrder || {}
                return {
                    purchaseOrderId: purchaseOrder.id,
                    purchaseOrderName: purchaseOrder.name || stockImport.supplierInvoiceNumber,
                    covaSupplierName: stockImport.covaSupplierName,
                    supplierName: stockImport.supplier && stockImport.supplier.name,
                    supplierCreated: Boolean(stockImport.supplierCreated),
                    rows: stockImport.rows,
                    units: stockImport.units,
                    supplierInvoiceNumber: stockImport.supplierInvoiceNumber,
                }
            })
        },
        importedProductCount() {
            const summary = (this.importResult && this.importResult.summary) || (this.preflight && this.preflight.summary) || {}
            return summary.productsToCreate || 0
        },
        completedSteps() {
            const steps = []
            if (this.filesReady) steps.push("upload")
            if (!this.isCatalogOnlyMode && this.mappingReady) steps.push("map")
            if (this.preflightEffectiveReady) steps.push("review")
            if (this.importResult) steps.push("import")
            if (!this.isCatalogOnlyMode && this.postedResult) steps.push("post")
            return steps
        },
        summaryMetrics() {
            const summary = (this.preflight && this.preflight.summary) || {}
            if (this.isCatalogOnlyMode) {
                return [
                    { label: "Catalog rows", value: this.numberFormat(summary.productCatalogRows) },
                    { label: "Rows with SKU", value: this.numberFormat(summary.productCatalogRowsWithSku) },
                    { label: "Existing products", value: this.numberFormat(summary.existingProducts) },
                    { label: "Products to create", value: this.numberFormat(summary.productsToCreate) },
                    {
                        label: this.form.imageStrategy === "mirror" ? "Images to mirror" : "Image references",
                        value: this.numberFormat(summary.mirrorImageCandidateCount),
                    },
                    { label: "Missing images", value: this.numberFormat(summary.missingImageCount) },
                    { label: "SKU conflicts", value: this.numberFormat(summary.supplierSkuConflictCount) },
                    { label: "Missing categories", value: this.numberFormat(summary.missingCategoryCount) },
                ]
            }
            return [
                { label: "Suppliers", value: this.numberFormat(summary.supplierCount) },
                { label: "Suppliers to create", value: this.numberFormat(this.supplierCreateCount) },
                { label: "Suppliers needing review", value: this.numberFormat(this.supplierNeedsReviewCount) },
                { label: "Purchase orders", value: this.numberFormat(summary.purchaseOrdersToCreate) },
                { label: "Unique SKUs", value: this.numberFormat(summary.uniqueInventoryCatalogSkus) },
                { label: "Units", value: this.numberFormat(summary.totalUnitsToPost) },
                { label: "Products to create", value: this.numberFormat(summary.productsToCreate) },
                { label: "Stock rows", value: this.numberFormat(summary.stockRowsToImport) },
                {
                    label: this.form.imageStrategy === "mirror" ? "Images to mirror" : "Image references",
                    value: this.numberFormat(summary.mirrorImageCandidateCount),
                },
                { label: "Missing images", value: this.numberFormat(summary.missingImageCount) },
                { label: "Blank package IDs", value: this.numberFormat(summary.blankPackageIdCount) },
                { label: "Existing package IDs", value: this.numberFormat(summary.existingPackageIdCount) },
                { label: "Skipped package IDs", value: this.numberFormat(summary.skippedExistingPackageIdCount) },
                { label: "Blank packaged dates", value: this.numberFormat(summary.blankPackagedDateCount) },
                { label: "Duplicate references", value: this.numberFormat(summary.duplicateImportReferenceCount) },
            ]
        },
    },
    watch: {
        warehouses() {
            if (this.preflight && !this.form.warehouseId) this.autoSelectWarehouseFromPreflight()
        },
        "form.supplierInvoiceNumber"() {
            if (this.preflight && !this.importHasSideEffects) this.clearAnalysis()
        },
        "form.imageStrategy"() {
            if (!this.importHasSideEffects) this.clearImportResults()
        },
        "form.fileMode"() {
            if (this.usesSingleWorkbook || this.isCatalogOnlyMode) {
                if (this.$refs.stockFileInput) this.$refs.stockFileInput.value = ""
                this.files.stockFile = null
            }
            if (!this.importHasSideEffects) this.clearAnalysis()
        },
        "form.allowDuplicatePackageIds"() {
            if (this.form.allowDuplicatePackageIds && this.form.skipExistingPackageIds) this.form.skipExistingPackageIds = false
            if (!this.importHasSideEffects) this.clearImportResults()
        },
        "form.skipExistingPackageIds"() {
            if (this.form.skipExistingPackageIds && this.form.allowDuplicatePackageIds) this.form.allowDuplicatePackageIds = false
            if (!this.importHasSideEffects) this.clearImportResults()
        },
    },
    mounted() {
        this.setData({ pageTitle: "Cova Import Wizard" })
        const handoff = consumeCovaSyncHandoff("import")
        if (handoff) {
            this.form.fileMode = handoff.fileMode || (handoff.stockFile ? "separate" : "catalog-only")
            this.form.warehouseId = handoff.warehouseId || ""
            this.files.productFile = handoff.productFile || null
            this.files.stockFile = handoff.stockFile || null
        }
    },
    beforeDestroy() {
        this.clearOperationProgressTimers()
    },
    methods: {
        ...mapActions("misc", ["setData", "fetchMainMasters"]),
        numberFormat(value) {
            const numberValue = Number(value || 0)
            return Number.isFinite(numberValue) ? numberValue.toLocaleString() : "0"
        },
        formatCountLabel(value, singularLabel, pluralLabel = `${singularLabel}s`) {
            const numberValue = Number(value || 0)
            return `${this.numberFormat(numberValue)} ${numberValue === 1 ? singularLabel : pluralLabel}`
        },
        joinHumanList(items) {
            if (items.length <= 2) return items.join(" and ")
            return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
        },
        hasBlockingCode(code) {
            const errors = (this.preflight && this.preflight.blockingErrors) || []
            return errors.some((issue) => issue.code === code)
        },
        clearOperationProgressTimers() {
            if (this.operationProgressTimer) {
                clearInterval(this.operationProgressTimer)
                this.operationProgressTimer = null
            }
            if (this.operationProgressClearTimer) {
                clearTimeout(this.operationProgressClearTimer)
                this.operationProgressClearTimer = null
            }
        },
        setOperationProgress(updates) {
            this.operationProgress = {
                ...this.operationProgress,
                ...updates,
            }
        },
        clearOperationProgress() {
            this.clearOperationProgressTimers()
            this.operationProgress = {
                active: false,
                label: "",
                detail: "",
                percent: 0,
            }
        },
        getImportProgressLabel(percent) {
            if (this.isCatalogOnlyMode) {
                if (percent < 35) return "Uploading Cova catalogue"
                if (percent < 72) return "Creating missing products"
                if (this.form.imageStrategy === "mirror" && percent < 90) return "Mirroring product images"
                return "Finalizing sync report"
            }
            if (percent < 25) return "Uploading Cova files"
            if (percent < 50) return "Validating supplier mappings"
            if (percent < 72) return "Creating products and purchase orders"
            if (this.form.imageStrategy === "mirror" && percent < 90) return "Mirroring product images"
            return "Finalizing import report"
        },
        getImportProgressDetail(percent) {
            const summary = (this.preflight && this.preflight.summary) || {}
            if (this.isCatalogOnlyMode) {
                if (percent < 72) return `${this.formatCountLabel(summary.productsToCreate, "product")} queued for catalogue sync.`
                if (this.form.imageStrategy === "mirror" && percent < 90) {
                    return `${this.formatCountLabel(summary.mirrorImageCandidateCount, "image")} queued for High Ties storage.`
                }
                return "Waiting for the server to return the catalogue sync report."
            }
            if (percent < 50) return `${this.formatCountLabel(summary.stockRowsToImport, "stock row")} queued for import.`
            if (percent < 72) {
                return `${this.formatCountLabel(summary.productsToCreate, "product")} and ${this.formatCountLabel(summary.purchaseOrdersToCreate, "purchase order")} in plan.`
            }
            if (this.form.imageStrategy === "mirror" && percent < 90) {
                return `${this.formatCountLabel(summary.mirrorImageCandidateCount, "image")} queued for High Ties storage.`
            }
            return "Waiting for the server to return the import report."
        },
        startImportProgress() {
            this.clearOperationProgressTimers()
            this.setOperationProgress({
                active: true,
                label: this.isCatalogOnlyMode ? "Preparing catalogue sync" : "Preparing import",
                detail: this.isCatalogOnlyMode
                    ? "Uploading the catalogue export and checking product rows."
                    : "Uploading files and checking the selected mappings.",
                percent: 8,
            })
            this.operationProgressTimer = setInterval(() => {
                const current = this.operationProgressPercent
                const increment = current < 45 ? 8 : current < 75 ? 5 : 3
                const nextPercent = Math.min(92, current + increment)
                this.setOperationProgress({
                    active: true,
                    label: this.getImportProgressLabel(nextPercent),
                    detail: this.getImportProgressDetail(nextPercent),
                    percent: nextPercent,
                })
            }, 1200)
        },
        updatePostProgress(postedCount, totalCount, purchaseOrder) {
            const total = Number(totalCount || 0)
            const posted = Number(postedCount || 0)
            const percent = total ? (posted / total) * 100 : 100
            const purchaseOrderName = purchaseOrder && (purchaseOrder.name || purchaseOrder.purchase_order_no || purchaseOrder.id)
            this.setOperationProgress({
                active: true,
                label: posted >= total ? "Inventory posted" : `Posting purchase order ${Math.min(posted + 1, total)} of ${total}`,
                detail: purchaseOrderName ? `Current PO: ${purchaseOrderName}` : `${posted} of ${total} purchase orders posted.`,
                percent,
            })
        },
        completeOperationProgress(label, detail) {
            this.clearOperationProgressTimers()
            this.setOperationProgress({
                active: true,
                label,
                detail,
                percent: 100,
            })
            this.operationProgressClearTimer = setTimeout(() => {
                if (!this.importing && !this.postingInventory) this.clearOperationProgress()
            }, 1200)
        },
        failOperationProgress(label, detail) {
            this.clearOperationProgressTimers()
            this.setOperationProgress({
                active: false,
                label,
                detail,
                percent: Math.max(1, this.operationProgressPercent),
            })
        },
        isAcknowledgedIssue(issue) {
            return (
                issue &&
                issue.code === "POSSIBLE_DUPLICATE_PACKAGE_IDS" &&
                (this.form.allowDuplicatePackageIds || this.form.skipExistingPackageIds)
            )
        },
        normalizeWarningIssue(issue, index) {
            const code = issue && typeof issue === "object" ? issue.code || issue.key || `warning-${index}` : `warning-${index}`
            const rawMessage =
                issue && typeof issue === "object"
                    ? issue.message || issue.description || issue.reason || issue.code
                    : issue
            const message = rawMessage ? String(rawMessage) : JSON.stringify(issue)
            return {
                id: `${code}-${index}`,
                code,
                message,
            }
        },
        normalizeWarningList(warnings) {
            if (!Array.isArray(warnings)) return []
            return warnings.map((warning, index) => this.normalizeWarningIssue(warning, index)).filter((warning) => warning.message)
        },
        uniqueWarnings(warnings) {
            const seen = new Set()
            return warnings.filter((warning) => {
                const key = `${warning.code}:${warning.message}`
                if (seen.has(key)) return false
                seen.add(key)
                return true
            })
        },
        extractResponseWarnings(res) {
            const data = (res && res.data) || {}
            const result = data.result || {}
            return this.uniqueWarnings([
                ...this.normalizeWarningList(data.warnings),
                ...this.normalizeWarningList(result.warnings),
            ])
        },
        collectImportResultWarnings(result) {
            if (!result) return []
            const stockImports = Array.isArray(result.stockImports) ? result.stockImports : []
            const stockImportWarnings = stockImports.reduce((warnings, stockImport) => {
                const nestedResult = stockImport && stockImport.result ? stockImport.result : {}
                return [
                    ...warnings,
                    ...this.normalizeWarningList(stockImport && stockImport.warnings),
                    ...this.normalizeWarningList(nestedResult.warnings),
                ]
            }, [])
            return this.uniqueWarnings([
                ...this.normalizeWarningList(result.warnings),
                ...this.normalizeWarningList(result.stockImport && result.stockImport.warnings),
                ...stockImportWarnings,
            ])
        },
        clearImportResults() {
            this.importResult = null
            this.postedResult = null
            this.postedPurchaseOrderResults = {}
            this.clearOperationProgress()
        },
        clearAnalysis() {
            this.preflight = null
            this.supplierMappings = {}
            this.form.allowDuplicatePackageIds = false
            this.form.skipExistingPackageIds = false
            this.clearImportResults()
            this.activeStep = "upload"
        },
        resetWizard() {
            if (this.importing || this.postingInventory) return
            this.preflightRequestId += 1
            this.importRequestId += 1
            if (this.$refs.productFileInput) this.$refs.productFileInput.value = ""
            if (this.$refs.stockFileInput) this.$refs.stockFileInput.value = ""
            this.files.productFile = null
            this.files.stockFile = null
            this.form.fileMode = "single"
            this.form.supplierInvoiceNumber = `COVA-IMPORT-${new Date().toISOString().slice(0, 10)}`
            this.form.notes = "Created from Cova import wizard"
            this.form.imageStrategy = "url"
            this.clearAnalysis()
        },
        getFileSignature(file) {
            if (!file) return ""
            return [file.name, file.size, file.lastModified, file.type].join(":")
        },
        getPreflightSignature() {
            return JSON.stringify({
                fileMode: this.form.fileMode,
                productFile: this.getFileSignature(this.files.productFile),
                stockFile: this.getFileSignature(this.effectiveStockFile),
                supplierInvoiceNumber: this.form.supplierInvoiceNumber,
                imageStrategy: this.form.imageStrategy,
                allowDuplicatePackageIds: Boolean(this.form.allowDuplicatePackageIds),
                skipExistingPackageIds: Boolean(this.form.skipExistingPackageIds),
            })
        },
        getImportSignature() {
            return JSON.stringify({
                preflight: this.getPreflightSignature(),
                warehouseId: this.form.warehouseId,
                supplierMappings: this.buildSupplierMappingsPayload()
                    .slice()
                    .sort((left, right) => left.covaSupplierName.localeCompare(right.covaSupplierName)),
            })
        },
        isCurrentPreflightRequest(requestId, signature) {
            return this.preflightRequestId === requestId && this.getPreflightSignature() === signature
        },
        isCurrentImportRequest(requestId, signature) {
            return this.importRequestId === requestId && this.getImportSignature() === signature
        },
        setWarehouse(warehouseId) {
            if (this.inputsLocked) return
            this.form.warehouseId = warehouseId || ""
            this.clearImportResults()
        },
        createEmptySupplierMapping() {
            return {
                action: "",
                supplierId: "",
                createSupplierName: "",
            }
        },
        isPlaceholderSupplierName(value) {
            const supplierName = String(value || "").trim()
            const normalizedName = supplierName.toLowerCase()
            if (!supplierName || normalizedName === "(blank)") return true
            if (["na", "n/a", "none", "null", "unknown", "unspecified", "-"].includes(normalizedName)) return true
            return false
        },
        isAmbiguousSupplierName(value) {
            const supplierName = String(value || "").trim()
            if (this.isPlaceholderSupplierName(supplierName)) return true
            return supplierName.replace(/[^a-z0-9]/gi, "").length < 3
        },
        isValidCreateSupplierName(value) {
            return !this.isPlaceholderSupplierName(value)
        },
        getCreateSupplierDefaultName(detectedSupplier) {
            const createSupplierName = detectedSupplier.createSupplierName || detectedSupplier.covaSupplierName || ""
            return this.isValidCreateSupplierName(createSupplierName) ? createSupplierName : ""
        },
        getSupplierMapping(covaSupplierName) {
            return this.supplierMappings[covaSupplierName] || this.createEmptySupplierMapping()
        },
        getDefaultSupplierMapping(supplier) {
            if (supplier.mappedSupplierId || supplier.matchedSupplierId) {
                return {
                    action: "existing",
                    supplierId: supplier.mappedSupplierId || supplier.matchedSupplierId,
                    createSupplierName: "",
                }
            }
            if (supplier.canCreateSupplier && supplier.createSupplierName && this.isValidCreateSupplierName(supplier.createSupplierName)) {
                return {
                    action: "create",
                    supplierId: "",
                    createSupplierName: supplier.createSupplierName,
                }
            }
            return this.createEmptySupplierMapping()
        },
        isSupplierMappingComplete(covaSupplierName) {
            const mapping = this.getSupplierMapping(covaSupplierName)
            if (mapping.action === "existing") return !!mapping.supplierId
            if (mapping.action === "create") return this.isValidCreateSupplierName(mapping.createSupplierName)
            return false
        },
        setSupplierMappingAction(covaSupplierName, action) {
            if (this.inputsLocked) return
            const current = this.getSupplierMapping(covaSupplierName)
            const detectedSupplier = this.detectedSuppliers.find((supplier) => supplier.covaSupplierName === covaSupplierName) || {}
            const next = {
                ...current,
                action: action || "",
            }
            if (next.action === "existing") {
                next.createSupplierName = ""
                next.supplierId = current.supplierId || detectedSupplier.matchedSupplierId || detectedSupplier.mappedSupplierId || ""
            } else if (next.action === "create") {
                next.supplierId = ""
                next.createSupplierName =
                    current.createSupplierName ||
                    this.getCreateSupplierDefaultName(detectedSupplier)
            } else {
                next.supplierId = ""
                next.createSupplierName = ""
            }
            this.$set(this.supplierMappings, covaSupplierName, next)
            this.clearImportResults()
        },
        setSupplierMappingSupplier(covaSupplierName, supplierId) {
            if (this.inputsLocked) return
            const current = this.getSupplierMapping(covaSupplierName)
            this.$set(this.supplierMappings, covaSupplierName, {
                ...current,
                action: "existing",
                supplierId: supplierId || "",
                createSupplierName: "",
            })
            this.clearImportResults()
        },
        setSupplierCreateName(covaSupplierName, supplierName) {
            if (this.inputsLocked) return
            const current = this.getSupplierMapping(covaSupplierName)
            this.$set(this.supplierMappings, covaSupplierName, {
                ...current,
                action: "create",
                supplierId: "",
                createSupplierName: supplierName || "",
            })
            this.clearImportResults()
        },
        handleFileSelect(field, event) {
            if (this.inputsLocked) return
            const file = event.target.files && event.target.files[0]
            this.files[field] = file || null
            if (field === "productFile" && this.usesSingleWorkbook) this.files.stockFile = null
            this.preflight = null
            this.clearImportResults()
            this.supplierMappings = {}
            this.form.allowDuplicatePackageIds = false
            this.form.skipExistingPackageIds = false
            this.activeStep = "upload"
        },
        buildSupplierMappingsPayload() {
            return this.detectedSuppliers.map((supplier) => {
                const mapping = this.getSupplierMapping(supplier.covaSupplierName)
                return {
                    covaSupplierName: supplier.covaSupplierName,
                    action: mapping.action || "",
                    supplierId: mapping.action === "existing" ? mapping.supplierId || "" : "",
                    createSupplierName: mapping.action === "create" ? mapping.createSupplierName || "" : "",
                }
            })
        },
            buildFormData() {
                const formData = new FormData()
                formData.append("productFile", this.files.productFile)
                if (!this.usesSingleWorkbook && !this.isCatalogOnlyMode) formData.append("stockFile", this.files.stockFile)
                formData.append("fileMode", this.form.fileMode)
                formData.append("importMode", this.isCatalogOnlyMode ? "catalog-only" : "full")
                if (!this.isCatalogOnlyMode) formData.append("location", this.form.warehouseId)
                formData.append("supplierInvoiceNumber", this.form.supplierInvoiceNumber)
            formData.append("notes", this.form.notes)
            formData.append("imageStrategy", this.form.imageStrategy)
            formData.append("allowDuplicatePackageIds", this.form.allowDuplicatePackageIds ? "1" : "0")
            formData.append("skipExistingPackageIds", this.form.skipExistingPackageIds ? "1" : "0")
            formData.append("supplierMappings", JSON.stringify(this.buildSupplierMappingsPayload()))
            const firstMappedSupplier = this.buildSupplierMappingsPayload().find(
                (mapping) => mapping.action === "existing" && !!mapping.supplierId
            )
            if (firstMappedSupplier) formData.append("supplier", firstMappedSupplier.supplierId)
            return formData
        },
        hydrateSupplierMappings(result) {
            const nextMappings = {}
            const resultSuppliers = (result && result.suppliers) || []
            resultSuppliers.forEach((supplier) => {
                const existingValue = this.supplierMappings[supplier.covaSupplierName]
                nextMappings[supplier.covaSupplierName] = existingValue || this.getDefaultSupplierMapping(supplier)
            })
            this.supplierMappings = nextMappings
        },
            autoSelectWarehouseFromPreflight() {
                if (this.isCatalogOnlyMode) return
                const locationText = this.detectedLocations.map((location) => location.covaLocationName).join(" ").toLowerCase()
            if (!locationText) return
            const tenthLineWarehouse = this.warehouses.find((warehouse) => {
                const name = String(warehouse.name || warehouse.title || "").toLowerCase()
                return locationText.includes("tenth line") && name.includes("tenth")
            })
            if (tenthLineWarehouse) this.form.warehouseId = tenthLineWarehouse.id
        },
        setPreflightResult(result) {
            this.preflight = result
            this.hydrateSupplierMappings(result)
            this.autoSelectWarehouseFromPreflight()
            this.importResult = null
            this.postedResult = null
        },
        async runPreflight() {
            if (!this.canRunPreflight) return
            const requestId = this.preflightRequestId + 1
            const signature = this.getPreflightSignature()
            this.preflightRequestId = requestId
            this.preflighting = true
            this.preflight = null
            try {
                    const res = await http.post("/cova-import/preflight", this.buildFormData())
                    if (!this.isCurrentPreflightRequest(requestId, signature)) return
                    this.setPreflightResult(res.data.result)
                    this.activeStep = this.isCatalogOnlyMode ? "review" : "map"
                    this.$toast.success(this.isCatalogOnlyMode ? "Catalogue analysis passed" : "Cova analysis passed")
            } catch (err) {
                if (!this.isCurrentPreflightRequest(requestId, signature)) return
                const result = err.response && err.response.data && err.response.data.result
                if (result) {
                    this.setPreflightResult(result)
                    this.activeStep = "review"
                    this.$toast.error("Cova analysis found issues to review")
                } else {
                    this.$toast.error(errMsg(err, "Cova analysis failed"))
                }
            } finally {
                if (this.preflightRequestId === requestId) this.preflighting = false
            }
        },
        async runImport() {
            if (!this.canImport || this.importing) return
            const requestId = this.importRequestId + 1
            const signature = this.getImportSignature()
            this.importRequestId = requestId
            this.importing = true
            this.startImportProgress()
            try {
                    const res = await http.post("/cova-import/run", this.buildFormData())
                    if (!this.isCurrentImportRequest(requestId, signature)) return
                    const result = res.data.result || {}
                    const warnings = this.uniqueWarnings([
                        ...this.normalizeWarningList(result.warnings),
                        ...this.extractResponseWarnings(res),
                    ])
                    this.importResult = {
                        ...result,
                        warnings,
                    }
                    if (this.createdSuppliers.length || this.isCatalogOnlyMode) {
                        await this.fetchMainMasters({ force: true, scope: "catalog" })
                    }
                    this.activeStep = "import"
                    this.completeOperationProgress(
                        this.isCatalogOnlyMode ? "Catalogue sync complete" : "Import complete",
                        this.isCatalogOnlyMode
                            ? "Products and images are synced. No stock was changed."
                            : "Products, suppliers, images, and purchase orders are ready for review."
                    )
                    if (this.importWarnings.length) {
                        this.$toast.warning("Import completed with warnings. Review before checkout.")
                    } else {
                        this.$toast.success(this.isCatalogOnlyMode ? "Catalogue sync completed" : "Cova import created purchase orders")
                    }
            } catch (err) {
                if (!this.isCurrentImportRequest(requestId, signature)) return
                this.failOperationProgress("Import stopped", errMsg(err, "Cova import failed"))
                this.$toast.error(errMsg(err, "Cova import failed"))
            } finally {
                if (this.importRequestId === requestId) this.importing = false
            }
        },
        async postInventory() {
            if (!this.purchaseOrders.length || this.postingInventory) return
            this.postingInventory = true
            const totalCount = this.purchaseOrders.length
            this.updatePostProgress(this.postedPurchaseOrderResultsList.length, totalCount, this.pendingPurchaseOrders[0])
            try {
                const results = [...this.postedPurchaseOrderResultsList]
                for (const purchaseOrder of this.purchaseOrders) {
                    if (this.postedPurchaseOrderResults[purchaseOrder.id]) continue
                    this.updatePostProgress(results.length, totalCount, purchaseOrder)
                    const res = await http.post(`/cova-import/purchase-orders/${purchaseOrder.id}/receive-complete`)
                    const result = res.data.result || {}
                    const warnings = this.uniqueWarnings([
                        ...this.normalizeWarningList(result.warnings),
                        ...this.extractResponseWarnings(res),
                    ])
                    const resultWithWarnings = {
                        ...result,
                        warnings,
                    }
                    this.$set(this.postedPurchaseOrderResults, purchaseOrder.id, resultWithWarnings)
                    results.push(resultWithWarnings)
                    this.updatePostProgress(results.length, totalCount, purchaseOrder)
                }
                const warnings = this.uniqueWarnings(
                    results.reduce((allWarnings, result) => [...allWarnings, ...this.normalizeWarningList(result.warnings)], [])
                )
                this.postedResult = {
                    purchaseOrders: results,
                    receivedRows: results.reduce((sum, result) => sum + Number(result.receivedRows || 0), 0),
                    receivedQty: results.reduce((sum, result) => sum + Number(result.receivedQty || 0), 0),
                    warnings,
                }
                this.activeStep = "post"
                this.completeOperationProgress("Inventory posted to POS", `${this.formatCountLabel(results.length, "purchase order")} received.`)
                if (warnings.length) {
                    this.$toast.warning("Inventory posted with warnings. Review before checkout.")
                } else {
                    this.$toast.success("Inventory posted to POS")
                }
            } catch (err) {
                const postedCount = this.postedPurchaseOrderResultsList.length
                const totalCount = this.purchaseOrders.length
                const fallbackMessage = postedCount
                    ? `Posted ${postedCount} of ${totalCount} purchase orders. Fix the error, then post remaining POs.`
                    : "Could not post inventory"
                this.failOperationProgress("Posting stopped", fallbackMessage)
                this.$toast.error(errMsg(err, fallbackMessage))
            } finally {
                this.postingInventory = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cova-import-page {
    display: grid;
    gap: 20px;
}

.wizard-header {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    padding-bottom: 18px;
    border-bottom: 1px solid $lightBorderColor;

    h2 {
        margin: 0;
        color: $primaryColor;
        font-size: 26px;
        font-weight: 700;
    }

    p {
        margin: 8px 0 0;
        max-width: 720px;
        color: rgba($textColor, 0.76);
        line-height: 1.55;
    }
}

.eyebrow {
    color: $secondaryColor;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.header-actions,
.action-row,
.report-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.step-rail {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 2px;
}

.step-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid $lightBorderColor;
    background: $lightCardBg;
    color: rgba($textColor, 0.74);
    border-radius: 999px;
    padding: 8px 12px;
    white-space: nowrap;
    transition: transform 0.18s ease, border-color 0.18s ease, color 0.18s ease;

    &:active {
        transform: translateY(1px);
    }

    &.active,
    &.done {
        border-color: rgba($primaryColor, 0.4);
        color: $primaryColor;
    }
}

.step-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: rgba($primaryColor, 0.1);
    font-size: 12px;
    font-weight: 700;
}

.section-block {
    border: 1px solid $lightBorderColor;
    border-radius: $cardRadius;
    padding: 18px;
    background: rgba(255, 255, 255, 0.62);
}

.section-heading {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;

    h3 {
        margin: 0;
        color: $textColor;
        font-size: 18px;
        font-weight: 700;
    }

    p {
        margin: 4px 0 0;
        color: rgba($textColor, 0.7);
    }
}

.section-status {
    align-self: flex-start;
    border: 1px solid $lightBorderColor;
    border-radius: 999px;
    padding: 6px 10px;
    color: rgba($textColor, 0.7);
    font-size: 12px;
    font-weight: 700;

    &.ready {
        border-color: rgba($successColor, 0.45);
        color: darken($successColor, 12%);
    }
}

.mode-warning {
    border: 1px solid rgba($warningColor, 0.35);
    border-radius: 8px;
    margin-bottom: 16px;
    padding: 10px 12px;
    background: rgba($warningColor, 0.12);
    color: darken($warningColor, 24%);
    font-size: 13px;
    font-weight: 700;
}

.form-grid,
.file-grid,
.facts-grid,
.metric-grid,
.operation-grid,
.report-columns,
.final-report {
    display: grid;
    gap: 14px;
}

.form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-grid--compact {
    align-items: end;
}

.form-grid--single {
    grid-template-columns: 1fr;
    margin-top: 16px;
}

.file-grid,
.operation-grid,
.report-columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 16px;
}

.facts-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.file-drop {
    display: grid;
    gap: 8px;
    border: 1px dashed rgba($primaryColor, 0.35);
    border-radius: $cardRadius;
    padding: 16px;
    cursor: pointer;
    background: rgba($primaryColor, 0.035);

    input {
        display: none;
    }

    &.file-drop--locked {
        border-style: solid;
        cursor: default;
        background: rgba($primaryColor, 0.02);
    }
}

.file-title,
.field-block label {
    font-size: 13px;
    font-weight: 700;
    color: $primaryColor;
}

.file-name {
    color: rgba($textColor, 0.74);
}

.field-block {
    display: grid;
    gap: 8px;
}

.policy-select {
    min-height: 40px;
    border: 1px solid $lightBorderColor;
    border-radius: 4px;
    padding: 8px 10px;
    color: $textColor;
    background: #fff;
    width: 100%;
}

.action-row {
    margin-top: 16px;
    justify-content: flex-end;
}

.operation-progress {
    margin-top: 16px;
    border: 1px solid rgba($primaryColor, 0.14);
    border-radius: $cardRadius;
    padding: 12px;
    background: rgba($primaryColor, 0.035);
}

.progress-heading,
.progress-detail {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.progress-heading {
    align-items: center;
    color: $primaryColor;
    font-size: 13px;
    font-weight: 700;

    strong {
        flex: 0 0 auto;
        font-size: 12px;
    }
}

.progress-track {
    width: 100%;
    height: 8px;
    margin-top: 9px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba($primaryColor, 0.12);
}

.progress-fill {
    height: 100%;
    border-radius: inherit;
    background: $primaryColor;
    transition: width 0.28s ease;
}

.progress-detail {
    margin-top: 8px;
    color: rgba($textColor, 0.66);
    font-size: 12px;
    line-height: 1.4;
}

.fact-panel {
    border-top: 1px solid $lightBorderColor;
    padding-top: 12px;

    strong {
        display: block;
        margin-top: 4px;
        color: $primaryColor;
        font-size: 18px;
    }

    p {
        margin: 5px 0 0;
        color: rgba($textColor, 0.68);
        line-height: 1.4;
    }
}

.mapping-row {
    margin-top: 16px;
    max-width: 520px;
}

.mapping-table,
.preview-table {
    margin-top: 16px;

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    th,
    td {
        border-top: 1px solid $lightBorderColor;
        padding: 9px 8px;
        text-align: left;
        vertical-align: middle;
    }

    th {
        color: rgba($textColor, 0.7);
        font-weight: 700;
    }
}

.mapping-table {
    overflow: visible;
}

.supplier-action-cell {
    min-width: 170px;
}

.supplier-choice-cell {
    min-width: 260px;
}

.mapping-action-select {
    min-height: 38px;
}

.create-supplier-field {
    display: grid;
    gap: 5px;
}

.supplier-name-input {
    width: 100%;
    min-height: 38px;
    border: 1px solid $lightBorderColor;
    border-radius: 4px;
    padding: 8px 10px;
    color: $textColor;
    background: #fff;

    &.invalid {
        border-color: rgba($dangerColor, 0.55);
    }

    &:focus {
        border-color: rgba($primaryColor, 0.65);
        outline: 0;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.12);
    }
}

.helper-text,
.mapping-help {
    display: block;
    color: rgba($textColor, 0.62);
    font-size: 12px;
    line-height: 1.35;
}

.preview-table {
    overflow-x: auto;
}

.status-chip {
    display: inline-flex;
    align-items: center;
    border: 1px solid $lightBorderColor;
    border-radius: 999px;
    padding: 4px 8px;
    color: rgba($textColor, 0.72);
    font-size: 12px;
    font-weight: 700;

    &.ready {
        border-color: rgba($successColor, 0.45);
        color: darken($successColor, 12%);
    }
}

.metric-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-cell {
    border-top: 1px solid $lightBorderColor;
    padding-top: 12px;

    span {
        display: block;
        color: rgba($textColor, 0.66);
        font-size: 12px;
    }

    strong {
        display: block;
        margin-top: 4px;
        color: $primaryColor;
        font-size: 22px;
    }
}

.issue-list {
    margin-top: 16px;
    border-radius: $cardRadius;
    padding: 12px;

    h4 {
        margin: 0 0 10px;
        font-size: 14px;
    }

    &.danger {
        background: rgba($dangerColor, 0.08);
        color: darken($dangerColor, 12%);
    }

    &.warning {
        background: rgba($warningColor, 0.12);
        color: darken($warningColor, 28%);
    }

    &.neutral {
        background: rgba($primaryColor, 0.06);
        color: $primaryColor;
    }
}

.issue-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;

    i {
        font-size: 18px;
    }
}

.ack-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 14px;
    color: $textColor;
    font-weight: 600;

    input {
        margin-top: 3px;
    }

    span {
        display: grid;
        gap: 3px;
    }

    small {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.35;
    }

    &--safe {
        color: darken($successColor, 14%);
    }
}

.duplicate-package-options {
    margin-top: 14px;
    border: 1px solid rgba($warningColor, 0.28);
    border-radius: $cardRadius;
    padding: 12px;
    background: rgba($warningColor, 0.06);

    .ack-row {
        margin-top: 0;
    }

    .ack-row + .ack-row {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid rgba($warningColor, 0.24);
    }
}

.preview-heading {
    color: $textColor;
    font-weight: 700;
    margin-bottom: 8px;
}

.operation-step {
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 12px;
    border-top: 1px solid $lightBorderColor;
    padding-top: 16px;

    h4 {
        margin: 0 0 6px;
        font-size: 15px;
        color: $textColor;
    }

    p {
        margin: 0 0 10px;
        color: rgba($textColor, 0.7);
    }
}

.operation-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba($primaryColor, 0.1);
    color: $primaryColor;

    &.done {
        background: rgba($successColor, 0.12);
        color: darken($successColor, 10%);
    }
}

.final-report {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    align-items: center;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid $lightBorderColor;

    strong {
        display: block;
        color: $primaryColor;
        font-size: 18px;
    }
}

.report-label {
    display: block;
    color: rgba($textColor, 0.64);
    font-size: 12px;
    font-weight: 700;
}

.report-actions {
    margin-top: 16px;
}

@media (max-width: 900px) {
    .wizard-header,
    .section-heading {
        flex-direction: column;
    }

    .form-grid,
    .file-grid,
    .facts-grid,
    .operation-grid,
    .metric-grid,
    .report-columns,
    .final-report {
        grid-template-columns: 1fr;
    }
}
</style>
